import {
  EyeOffIcon,
  GemIcon,
  MegaphoneIcon,
  SparklesIcon,
  SwordsIcon,
  TelescopeIcon,
} from 'lucide-react';

import type { BrandXRayDto } from '@/entities/analysis';
import { AnalysisSubHeader } from '@/pages/AnalysisResult/components';
import { CulturalTruthCard, type CulturalTruth } from './CulturalTruthCard';
import { useTranslation } from 'react-i18next';

import { hasContent } from '../../../utils/hasContent';

const CULTURAL_TRUTHS = [
  {
    key: 'sacredCows',
    titleKey: 'analysisResult.culturalAnalysisItems.prizedPrinciples',
    icon: GemIcon,
    span: 3,
  },
  {
    key: 'surprisingTruths',
    titleKey: 'analysisResult.culturalAnalysisItems.surprisingTruths',
    icon: SparklesIcon,
    span: 3,
  },
  {
    key: 'elephantInRoom',
    titleKey: 'analysisResult.culturalAnalysisItems.elephantInTheRoom',
    icon: EyeOffIcon,
    span: 6,
    filled: true,
  },
  {
    key: 'swornEnemy',
    titleKey: 'analysisResult.culturalAnalysisItems.swornEnemy',
    icon: SwordsIcon,
    span: 2,
  },
  {
    key: 'unexpectedEndorser',
    titleKey: 'analysisResult.culturalAnalysisItems.unexpectedEndorser',
    icon: MegaphoneIcon,
    span: 2,
  },
  {
    key: 'futureRedefinition',
    titleKey: 'analysisResult.culturalAnalysisItems.futureRedefinition',
    icon: TelescopeIcon,
    span: 2,
  },
] as const satisfies readonly CulturalTruth[];

export const hasCulturalTruths = (brandXRay: BrandXRayDto | undefined) =>
  CULTURAL_TRUTHS.some(({ key }) => hasContent(brandXRay?.[key]));

export const CulturalAnalysisItems = ({ brandXRay }: { brandXRay: BrandXRayDto | undefined }) => {
  const { t } = useTranslation('analysis');

  const items = CULTURAL_TRUTHS.map((config) => ({
    config,
    content: brandXRay?.[config.key],
  })).filter(({ content }) => hasContent(content));

  if (items.length === 0) return null;

  return (
    <div className="flex flex-col gap-6">
      <AnalysisSubHeader
        title={t('analysisResult.culturalAnalysisItems.culturalTruths')}
        subtitle={t('analysisResult.culturalAnalysisItems.culturalTruthsSubtitle')}
        subtitleTone="meta"
      />
      <div className="grid grid-cols-1 items-stretch gap-3 lg:auto-rows-[minmax(200px,auto)] lg:grid-cols-6">
        {items.map(({ config, content }) => (
          <CulturalTruthCard
            key={config.key}
            config={config}
            title={t(config.titleKey)}
            content={content}
          />
        ))}
      </div>
    </div>
  );
};
